import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import { CheckCircleIcon, MinusIcon, TimeIcon, WarningIcon } from "@chakra-ui/icons";
import { FaCalendarAlt } from "react-icons/fa";
import {
  AnaliseCredito,
  AnaliseCreditoPasso as AnaliseCreditoPassoType,
} from "@/models/consignado/report-data.model";

export const NOME_MOTOR_1 = "Motor 1 (Listar Autorizados)";
export const NOME_MOTOR_BMP = "Motor dados BMP (margem e dados cadastrais)";

export const semVazios = (passos: (AnaliseCreditoPassoType | undefined)[]) =>
  passos.filter((passo): passo is AnaliseCreditoPassoType => Boolean(passo));

// Passos da análise de crédito que entram no resultado de cada motor, pelo
// nome do motor: motor 1 → critérios mínimos; motor BMP → critérios totais.
const PASSOS_POR_MOTOR: Record<string, (analise: AnaliseCredito) => (AnaliseCreditoPassoType | undefined)[]> = {
  [NOME_MOTOR_1]: (analise) => [analise.validacoes_cnpj_cpf?.criterios_dados_minimos],
  [NOME_MOTOR_BMP]: (analise) => [analise.validacoes_cnpj_cpf?.criterios_dados_totais],
};

export const passosDoMotor = (
  analiseCredito: AnaliseCredito | null | undefined,
  nomeMotor: string | null | undefined
): AnaliseCreditoPassoType[] => {
  if (!analiseCredito || !nomeMotor) return [];
  return semVazios(PASSOS_POR_MOTOR[nomeMotor]?.(analiseCredito) ?? []);
};

// Passos do motor de originação. Com o Score C1 aprovado, o Turnover e o Score
// C2 não rodam e ficariam "aguardando" para sempre; nesse caso, aparecem como
// "Não aplicado".
const PASSOS_DEPOIS_DO_C1 = ["turnover", "score_c2"];

// Passo como é exibido: naoAplicado troca o "Aguardando" por "Não aplicado".
export type AnaliseCreditoPassoExibido = AnaliseCreditoPassoType & { naoAplicado?: boolean };

// Ordem em que o back roda os passos (consultaCredito): validações de CNPJ e
// CPF, motor de originação e enquadramento. Um erro interrompe a análise, e os
// passos seguintes ficam "aguardando" para sempre — exceto o Score C1, que,
// reprovado, passa a decisão para o Score C2.
const ORDEM_DOS_PASSOS: { [B in keyof AnaliseCredito]: (keyof AnaliseCredito[B])[] } = {
  validacoes_cnpj_cpf: [
    "criterios_dados_minimos",
    "aprovacao_pj",
    "quantidade_funcionarios",
    "consulta_margem",
    "criterios_dados_totais",
  ],
  motor_originacao: [
    "score_c1",
    "turnover",
    "score_c2",
    "divida_fgts",
    "situacao_cpf_receita",
    "situacao_cnpj_receita",
  ],
  enquadramento: ["enquadramento_risco", "produtos_validos"],
};
const ERROS_QUE_NAO_INTERROMPEM = ["score_c1"];

// Análise com os passos que não rodam por causa de um erro anterior marcados
// como "Não aplicado".
export const comPassosNaoAplicados = <T extends AnaliseCredito | null | undefined>(analise: T): T => {
  if (!analise) return analise;
  let interrompida = false;
  const resultado: Record<string, Record<string, AnaliseCreditoPassoExibido>> = {};
  for (const [bloco, chaves] of Object.entries(ORDEM_DOS_PASSOS) as [keyof AnaliseCredito, string[]][]) {
    const passos = analise[bloco] as Record<string, AnaliseCreditoPassoType> | undefined;
    if (!passos) continue;
    const exibidos: Record<string, AnaliseCreditoPassoExibido> = { ...passos };
    for (const chave of chaves) {
      const passo = passos[chave];
      if (!passo) continue;
      if (interrompida && passo.status === "aguardando") exibidos[chave] = { ...passo, naoAplicado: true };
      if (passo.status === "erro" && !ERROS_QUE_NAO_INTERROMPEM.includes(chave)) interrompida = true;
    }
    resultado[bloco] = exibidos;
  }
  return { ...analise, ...resultado };
};

export const passosDoMotorOriginacao = (
  motorOriginacao: AnaliseCredito["motor_originacao"]
): AnaliseCreditoPassoExibido[] => {
  const c1Aprovado = motorOriginacao.score_c1?.status === "sucesso";
  return semVazios(
    Object.entries(motorOriginacao).map(([chave, passo]) =>
      passo && c1Aprovado && PASSOS_DEPOIS_DO_C1.includes(chave) && passo.status === "aguardando"
        ? { ...passo, naoAplicado: true }
        : passo
    )
  );
};

// A mensagem do passo vem prefixada com "dd/MM/yyyy HH:mm: "; a data já
// aparece no cabeçalho do relatório, então não repete.
const DATA_HORA_NO_INICIO = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}: /;

const DATA_HORA_DO_PASSO = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}:\d{2}): /;

// Data e hora do bloco, no formato do cabeçalho dos motores
// ("dd/MM/yyyy, HH:mm"): a do passo mais recente, tirada da mensagem.
export const dataHoraDosPassos = (passos: AnaliseCreditoPassoType[]): string | undefined => {
  const datas = passos
    .map((passo) => passo.mensagem?.match(DATA_HORA_DO_PASSO))
    .filter((data): data is RegExpMatchArray => Boolean(data))
    .map(([, dia, mes, ano, hora]) => ({ ordem: `${ano}${mes}${dia}${hora}`, texto: `${dia}/${mes}/${ano}, ${hora}` }));
  if (datas.length === 0) return undefined;
  return datas.reduce((maisRecente, data) => (data.ordem > maisRecente.ordem ? data : maisRecente)).texto;
};

// Cabeçalho no mesmo formato do card de motor: nome e data/hora na mesma
// linha, descrição embaixo. rotuloDescricaoMotor põe "Nome Motor:" e "Descrição
// Motor:" como nos cards de motor; sem ele (bloco que não é motor, ex.:
// validações de CNPJ e CPF), o nome vai em negrito, sem rótulo.
export function AnaliseCreditoCabecalho({
  nome,
  descricao,
  dataHora,
  rotuloDescricaoMotor,
  children,
}: {
  nome: string;
  descricao: string;
  dataHora?: string;
  rotuloDescricaoMotor?: boolean;
  children?: ReactNode;
}) {
  return (
    <VStack align="start" spacing={0} w="100%" pr={4}>
      <HStack spacing={1} w="100%" justifyContent="space-between">
        <Text
          fontSize="xs"
          color="gray.600"
          fontWeight={rotuloDescricaoMotor ? "normal" : "bold"}
        >
          {rotuloDescricaoMotor && (
            <>
              <Text as="span" fontWeight="bold">
                Nome Motor:
              </Text>{" "}
            </>
          )}
          {nome}
        </Text>

        {dataHora && (
          <HStack spacing={1}>
            <Icon as={FaCalendarAlt} color="gray.500" boxSize={2.5} />
            <Text fontSize="xs" color="gray.600">
              {dataHora}
            </Text>
          </HStack>
        )}
      </HStack>

      <Text fontSize="xs" color="gray.600" textAlign="left">
        {rotuloDescricaoMotor && (
          <>
            <Text as="span" fontWeight="bold">
              Descrição Motor:
            </Text>{" "}
          </>
        )}
        {descricao}
      </Text>

      {children}
    </VStack>
  );
}

const textoPorStatus: Partial<Record<AnaliseCreditoPassoType["status"], string>> = {
  aguardando: "Aguardando",
  cancelado: "Cancelado",
};

// Enquanto o passo não roda, a mensagem é só o nome dele ("Score C1").
const textoDoPasso = (passo: AnaliseCreditoPassoExibido) => {
  const mensagem = passo.mensagem?.replace(DATA_HORA_NO_INICIO, "");
  const statusPendente = passo.naoAplicado ? "Não aplicado" : textoPorStatus[passo.status];
  return statusPendente ? `${mensagem}: ${statusPendente}` : mensagem;
};

// Mesmos ícones e cores do Histórico da proposta (ProposalLogs), que usa o
// mesmo enum de status do custom_status.
const visualPorStatus = {
  sucesso: { icone: CheckCircleIcon, cor: "green.500" },
  erro: { icone: WarningIcon, cor: "red.500" },
  aguardando: { icone: TimeIcon, cor: "blue.400" },
  cancelado: { icone: WarningIcon, cor: "red.500" },
};

// Passo que não roda (ex.: Turnover e Score C2 com o C1 aprovado): neutro, sem
// o relógio de "aguardando".
const visualNaoAplicado = { icone: MinusIcon, cor: "gray.400" };

// Status do bloco no círculo do card: erro se algum passo falhou; aguardando
// se nenhum rodou; senão sucesso.
const statusDoBloco = (passos: AnaliseCreditoPassoType[]): AnaliseCreditoPassoType["status"] => {
  if (passos.some((passo) => passo.status === "erro")) return "erro";
  if (passos.every((passo) => passo.status === "aguardando")) return "aguardando";
  return "sucesso";
};

// Ícone e cor do círculo de um card a partir dos passos do bloco. Passos "Não
// aplicado" não contam; bloco em que nenhum se aplica fica neutro, como o passo.
export const visualDoBloco = (passos: AnaliseCreditoPassoExibido[]) => {
  const aplicados = passos.filter((passo) => !passo.naoAplicado);
  if (passos.length > 0 && aplicados.length === 0) return visualNaoAplicado;
  return visualPorStatus[statusDoBloco(aplicados)];
};

// Passos da análise, no mesmo formato da lista de "CRITÉRIOS DE ANÁLISE" dos
// motores.
export function AnaliseCreditoResultado({
  passos,
  mt,
}: {
  passos: AnaliseCreditoPassoExibido[];
  mt?: number;
}) {
  return (
    <Box mt={mt}>
      <VStack align="stretch" spacing={2}>
        {passos.map((passo, indice) => (
          <AnaliseCreditoItem key={indice} passo={passo} />
        ))}
      </VStack>
    </Box>
  );
}

// Um passo no formato dos itens de "CRITÉRIOS DE ANÁLISE".
export function AnaliseCreditoItem({ passo }: { passo: AnaliseCreditoPassoExibido }) {
  const visual = passo.naoAplicado
    ? visualNaoAplicado
    : visualPorStatus[passo.status] ?? visualPorStatus.aguardando;
  return (
    <Box
      p={2}
      bg="gray.50"
      borderRadius="md"
      border="1px solid"
      borderColor="gray.200"
    >
      <HStack spacing={2}>
        <Icon as={visual.icone} color={visual.cor} boxSize={3} />
        <Text fontSize="xs" fontWeight="semibold" color="gray.700">
          {textoDoPasso(passo)}
        </Text>
      </HStack>
    </Box>
  );
}

// Card de um bloco da análise de crédito, no mesmo formato dos cards de motor:
// círculo de status + título; ao abrir, os passos (ou o conteúdo passado em
// children, ex.: os cards dos motores dentro das validações de CNPJ e CPF).
export function AnaliseCreditoBlocoCard({
  titulo,
  descricao,
  passos,
  rotuloDescricaoMotor,
  children,
}: {
  titulo: string;
  descricao: string;
  passos: AnaliseCreditoPassoExibido[];
  rotuloDescricaoMotor?: boolean;
  children?: ReactNode;
}) {
  const visual = visualDoBloco(passos);

  return (
    <Accordion
      allowToggle
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
    >
      <AccordionItem border="none">
        <AccordionButton p={3} _hover={{ bg: "gray.50" }} borderRadius="md">
          <Flex flex={1} align="center" justify="space-between" w="100%">
            <HStack spacing={2} w="100%">
              <Box
                bg={visual.cor}
                borderRadius="full"
                p={1.5}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={visual.icone} color="white" boxSize={3} />
              </Box>
              <AnaliseCreditoCabecalho
                nome={titulo}
                descricao={descricao}
                dataHora={dataHoraDosPassos(passos)}
                rotuloDescricaoMotor={rotuloDescricaoMotor}
              />
            </HStack>
            <AccordionIcon />
          </Flex>
        </AccordionButton>

        <AccordionPanel pb={3} px={3}>
          <Divider mb={3} />
          {children ?? <AnaliseCreditoResultado passos={passos} />}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}
